// Contact list origins other than HubSpot: spreadsheet and hand-typed.
//
// The shape produced here is what the dispatch endpoint reads — `dispatch_contacts` in
// OutboundsController permits contact_id, first_name, last_name, email, phone, company,
// job_title — so neither origin needs a backend change.

// Same threshold the Dispatcher uses to decide whether a contact is dispatchable
// (`dispatcher.rb`: gsub(/[^0-9+]/,'').length >= 8). The real E.164 normalization happens
// server-side; this only answers "is there enough here to try".
const MIN_PHONE_LENGTH = 8;

// Header aliases, lowercased. A spreadsheet built by a salesperson rarely uses the exact
// column name the docs ask for.
const COLUMNS = {
  name: ['nome', 'name', 'contato'],
  phone: ['telefone', 'phone', 'celular', 'whatsapp', 'fone'],
  email: ['email', 'e-mail'],
  company: ['empresa', 'company'],
};

export function sanitizePhone(raw) {
  return String(raw || '').replace(/[^0-9+]/g, '');
}

export function hasUsablePhone(raw) {
  return sanitizePhone(raw).length >= MIN_PHONE_LENGTH;
}

// Excel in pt-BR exports CSV with `;`. Guessing from the header row beats asking the
// operator to re-export.
function detectSeparator(headerLine) {
  const semicolons = (headerLine.match(/;/g) || []).length;
  const commas = (headerLine.match(/,/g) || []).length;
  return semicolons > commas ? ';' : ',';
}

// Quote-aware split: a quoted field may contain the separator, and `""` is an escaped
// quote inside a quoted field.
export function parseCsvLine(line, separator = ',') {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"' && inQuotes && line[i + 1] === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === separator && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  values.push(current.trim());

  return values;
}

function indexOfColumn(headers, aliases) {
  return headers.findIndex(header => aliases.includes(header));
}

function buildContact({ id, name, phone, email = '', company = '' }) {
  const [firstName, ...rest] = String(name || '')
    .trim()
    .split(' ');

  return {
    contact_id: id,
    first_name: firstName || '',
    last_name: rest.join(' '),
    email,
    phone: sanitizePhone(phone),
    company,
    job_title: '',
    has_phone: hasUsablePhone(phone),
  };
}

// Returns { contacts, skipped, error }. `error` is an i18n suffix so the caller owns the
// copy; `skipped` is the number of dropped rows, reported rather than swallowed — silently
// losing rows is how a campaign quietly reaches fewer people than the operator believes.
export function contactsFromCsv(text) {
  // Excel writes a UTF-8 BOM; left in place it becomes part of the first header name and
  // the Name column stops matching. Escaped rather than literal so the source stays ASCII.
  const clean = String(text || '').replace(/^\uFEFF/, '');
  const lines = clean.split(/\r?\n/).filter(line => line.trim());

  if (lines.length < 2) return { contacts: [], skipped: 0, error: 'EMPTY' };

  const separator = detectSeparator(lines[0]);
  const headers = parseCsvLine(lines[0], separator).map(header =>
    header.toLowerCase().trim()
  );

  const nameIndex = indexOfColumn(headers, COLUMNS.name);
  const phoneIndex = indexOfColumn(headers, COLUMNS.phone);
  if (nameIndex === -1 || phoneIndex === -1) {
    return { contacts: [], skipped: 0, error: 'MISSING_COLUMNS' };
  }

  const emailIndex = indexOfColumn(headers, COLUMNS.email);
  const companyIndex = indexOfColumn(headers, COLUMNS.company);
  const at = (values, index) =>
    index === -1 ? '' : String(values[index] || '').trim();

  const stamp = Date.now();
  const contacts = [];
  let skipped = 0;

  lines.slice(1).forEach((line, index) => {
    const values = parseCsvLine(line, separator);
    const name = at(values, nameIndex);
    const phone = at(values, phoneIndex);

    // A row needs both: name-only would land in the "missing phone" bucket and never be
    // sent, phone-only gives the operator nothing to recognize in the table.
    if (!name || !phone) {
      skipped += 1;
      return;
    }

    contacts.push(
      buildContact({
        id: `csv-${stamp}-${index}`,
        name,
        phone,
        email: at(values, emailIndex),
        company: at(values, companyIndex),
      })
    );
  });

  if (!contacts.length)
    return { contacts: [], skipped, error: 'NO_VALID_ROWS' };

  return { contacts, skipped, error: null };
}

// Hand-typed rows. A row counts as filled when it has a name or a phone; rows the operator
// left blank are ignored rather than rejected.
export function contactsFromManualRows(rows) {
  const stamp = Date.now();

  return rows
    .filter(
      row => String(row.name || '').trim() || String(row.phone || '').trim()
    )
    .map((row, index) =>
      buildContact({
        id: `manual-${stamp}-${index}`,
        name: row.name,
        phone: row.phone,
      })
    );
}
